import { useState, useEffect, useCallback } from 'react';
import { API_BASE_URL } from './config';

interface AuthUser {
  id: string;
  name: string;
  email: string;
  role: 'host' | 'admin';
}

interface NoticeComment {
  id: number;
  user_id: string;
  user_name: string;
  user_role: string;
  content: string;
  created_at: string;
}

interface NoticeItem {
  id: number;
  title: string;
  content: string;
  user_id: string;
  user_name: string;
  user_role: string;
  is_pinned: boolean;
  created_at: string;
  comment_count: number;
  comments: NoticeComment[];
}

interface Props {
  token: string;
  user: AuthUser | null;
}

function maskName(name: string): string {
  if (name.length <= 1) return name;
  if (name.length === 2) return name[0] + 'O';
  return name[0] + 'O'.repeat(name.length - 2) + name[name.length - 1];
}

function fmtDate(iso: string) {
  if (!iso) return '';
  const utc = iso.endsWith('Z') || iso.includes('+') ? iso : iso + 'Z';
  const d = new Date(utc);
  return d.toLocaleDateString('ko-KR', { year: '2-digit', month: '2-digit', day: '2-digit' });
}

export default function NoticeBoard({ token, user }: Props) {
  const [notices, setNotices] = useState<NoticeItem[]>([]);
  const [expandedId, setExpandedId] = useState<number | null>(null);
  const [commentInputs, setCommentInputs] = useState<Record<number, string>>({});
  const [showWrite, setShowWrite] = useState(false);
  const [writeForm, setWriteForm] = useState({ title: '', content: '', is_pinned: false });
  const [submitting, setSubmitting] = useState(false);

  const isAdmin = user?.role === 'admin';
  const headers: Record<string, string> = { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' };

  const fetchNotices = useCallback(async () => {
    try {
      const res = await fetch(`${API_BASE_URL}/api/notices`, { headers });
      if (res.ok) setNotices(await res.json());
    } catch {}
  }, [token]);

  useEffect(() => { fetchNotices(); }, [fetchNotices]);

  const pinnedNotices = notices.filter((n) => n.is_pinned).slice(0, 5);
  const generalNotices = notices.filter((n) => !n.is_pinned).slice(0, 20);

  const handleWrite = async () => {
    if (!writeForm.title.trim() || !writeForm.content.trim()) {
      alert('제목과 내용을 모두 입력하세요'); return;
    }
    setSubmitting(true);
    try {
      const res = await fetch(`${API_BASE_URL}/api/notices`, {
        method: 'POST', headers, body: JSON.stringify(writeForm),
      });
      if (res.ok) {
        setShowWrite(false);
        setWriteForm({ title: '', content: '', is_pinned: false });
        fetchNotices();
      }
    } finally { setSubmitting(false); }
  };

  const handleDelete = async (id: number) => {
    if (!window.confirm('이 글을 삭제하시겠습니까?')) return;
    await fetch(`${API_BASE_URL}/api/notices/${id}`, { method: 'DELETE', headers });
    if (expandedId === id) setExpandedId(null);
    fetchNotices();
  };

  const handleComment = async (noticeId: number) => {
    const content = (commentInputs[noticeId] || '').trim();
    if (!content) return;
    const res = await fetch(`${API_BASE_URL}/api/notices/${noticeId}/comments`, {
      method: 'POST', headers, body: JSON.stringify({ content }),
    });
    if (res.ok) {
      setCommentInputs((p) => ({ ...p, [noticeId]: '' }));
      fetchNotices();
    }
  };

  const handleDeleteComment = async (noticeId: number, commentId: number) => {
    await fetch(`${API_BASE_URL}/api/notices/${noticeId}/comments/${commentId}`, {
      method: 'DELETE', headers,
    });
    fetchNotices();
  };

  const renderNotice = (n: NoticeItem) => (
    <div key={n.id} style={{ background: '#fff', border: '1px solid #e8ecf0', borderRadius: 12, marginBottom: 10, overflow: 'hidden' }}>
      <div style={{ padding: '16px 20px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 6 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            {n.is_pinned && (
              <span style={{ fontSize: 10, fontWeight: 700, color: '#b45309', background: '#fef3c7', padding: '2px 6px', borderRadius: 4 }}>공지</span>
            )}
            <span style={{ fontSize: 11, color: '#94a3b8' }}>{fmtDate(n.created_at)}</span>
            <span style={{ fontSize: 11, color: '#cbd5e1' }}>·</span>
            <span style={{ fontSize: 11, color: n.user_role === 'admin' ? '#2563eb' : '#94a3b8', fontWeight: n.user_role === 'admin' ? 600 : 400 }}>
              {n.user_role === 'admin' ? '관리자' : maskName(n.user_name)}
            </span>
          </div>
          {(isAdmin || user?.id === n.user_id) && (
            <button
              onClick={() => handleDelete(n.id)}
              style={{ fontSize: 11, color: '#dc2626', background: 'none', border: '1px solid #fca5a5', borderRadius: 4, padding: '2px 8px', cursor: 'pointer' }}
            >삭제</button>
          )}
        </div>
        <div
          onClick={() => setExpandedId(expandedId === n.id ? null : n.id)}
          style={{ cursor: 'pointer' }}
        >
          <div style={{ fontSize: 14, fontWeight: 700, color: '#0f172a', marginBottom: expandedId === n.id ? 8 : 0 }}>{n.title}</div>
          {expandedId === n.id && (
            <div style={{ fontSize: 13, color: '#475569', lineHeight: 1.7, whiteSpace: 'pre-wrap' }}>{n.content}</div>
          )}
        </div>
      </div>

      {/* 댓글 토글 */}
      <div
        onClick={() => setExpandedId(expandedId === n.id ? null : n.id)}
        style={{
          padding: '8px 20px', borderTop: '1px solid #f1f5f9',
          display: 'flex', alignItems: 'center', gap: 6,
          cursor: 'pointer', background: '#fafafa', userSelect: 'none',
        }}
      >
        <span style={{ fontSize: 12, color: '#64748b' }}>댓글 {n.comment_count}개</span>
        <span style={{ fontSize: 11, color: '#94a3b8' }}>{expandedId === n.id ? '▲' : '▼'}</span>
      </div>

      {/* 댓글 섹션 */}
      {expandedId === n.id && (
        <div style={{ padding: '12px 20px 16px', borderTop: '1px solid #f1f5f9', background: '#f8fafc' }}>
          {n.comments.map((c) => (
            <div key={c.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', padding: '8px 0', borderBottom: '1px solid #f1f5f9' }}>
              <div>
                <span style={{
                  fontSize: 12, fontWeight: 600, marginRight: 8,
                  color: c.user_role === 'admin' ? '#2563eb' : '#334155',
                }}>
                  {c.user_role === 'admin' ? '관리자' : maskName(c.user_name)}
                </span>
                <span style={{ fontSize: 12, color: '#475569' }}>{c.content}</span>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexShrink: 0, marginLeft: 12 }}>
                <span style={{ fontSize: 11, color: '#cbd5e1' }}>{fmtDate(c.created_at)}</span>
                {(user?.id === c.user_id || isAdmin) && (
                  <button
                    onClick={() => handleDeleteComment(n.id, c.id)}
                    style={{ fontSize: 11, color: '#94a3b8', background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}
                  >삭제</button>
                )}
              </div>
            </div>
          ))}
          <div style={{ display: 'flex', gap: 8, marginTop: 10 }}>
            <input
              style={{ flex: 1, padding: '8px 12px', border: '1px solid #e2e8f0', borderRadius: 8, fontSize: 13, background: '#fff' }}
              placeholder="댓글을 입력하세요..."
              value={commentInputs[n.id] || ''}
              onChange={(e) => setCommentInputs((p) => ({ ...p, [n.id]: e.target.value }))}
              onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleComment(n.id); } }}
            />
            <button
              onClick={() => handleComment(n.id)}
              style={{ padding: '8px 14px', background: '#2563eb', color: '#fff', border: 'none', borderRadius: 8, fontSize: 13, cursor: 'pointer', whiteSpace: 'nowrap' }}
            >등록</button>
          </div>
        </div>
      )}
    </div>
  );

  return (
    <div style={{ maxWidth: 760, margin: '0 auto', padding: '24px 16px' }}>
      {/* 헤더 */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
        <h2 style={{ fontSize: 18, fontWeight: 700, color: '#0f172a', margin: 0 }}>정보 / 소식</h2>
        <div style={{ display: 'flex', gap: 8 }}>
          {isAdmin && (
            <button
              onClick={() => { setWriteForm({ title: '', content: '', is_pinned: true }); setShowWrite(true); }}
              style={{
                padding: '7px 14px', background: '#f59e0b', color: '#fff',
                border: 'none', borderRadius: 8, fontSize: 13, fontWeight: 500, cursor: 'pointer',
              }}
            >+ 공지 등록</button>
          )}
          <button
            onClick={() => { setWriteForm({ title: '', content: '', is_pinned: false }); setShowWrite(true); }}
            style={{
              padding: '7px 14px', background: '#2563eb', color: '#fff',
              border: 'none', borderRadius: 8, fontSize: 13, fontWeight: 500, cursor: 'pointer',
            }}
          >+ 글쓰기</button>
        </div>
      </div>

      {/* 작성 모달 */}
      {showWrite && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
          onClick={(e) => { if (e.target === e.currentTarget) setShowWrite(false); }}
        >
          <div style={{ background: '#fff', borderRadius: 12, padding: 24, width: 'min(92vw, 500px)', boxSizing: 'border-box' }}>
            <h3 style={{ fontSize: 16, fontWeight: 600, margin: '0 0 16px' }}>
              {writeForm.is_pinned ? '공지 등록' : '글쓰기'}
            </h3>
            <div style={{ marginBottom: 12 }}>
              <label style={{ display: 'block', fontSize: 13, color: '#888', marginBottom: 4 }}>제목</label>
              <input
                style={{ width: '100%', padding: '9px 12px', border: '1px solid #e2e5e9', borderRadius: 8, fontSize: 14, boxSizing: 'border-box' }}
                value={writeForm.title}
                onChange={(e) => setWriteForm((p) => ({ ...p, title: e.target.value }))}
                placeholder="제목을 입력하세요"
              />
            </div>
            <div style={{ marginBottom: 16 }}>
              <label style={{ display: 'block', fontSize: 13, color: '#888', marginBottom: 4 }}>내용</label>
              <textarea
                style={{ width: '100%', padding: '9px 12px', border: '1px solid #e2e5e9', borderRadius: 8, fontSize: 14, boxSizing: 'border-box', minHeight: 120, resize: 'vertical' }}
                value={writeForm.content}
                onChange={(e) => setWriteForm((p) => ({ ...p, content: e.target.value }))}
                placeholder="내용을 입력하세요..."
              />
            </div>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
              <button onClick={() => setShowWrite(false)} style={{ padding: '8px 16px', border: '1px solid #e2e5e9', borderRadius: 8, background: '#f5f5f5', color: '#333', fontSize: 13, cursor: 'pointer' }}>취소</button>
              <button onClick={handleWrite} disabled={submitting} style={{ padding: '8px 16px', border: 'none', borderRadius: 8, background: writeForm.is_pinned ? '#f59e0b' : '#2563eb', color: '#fff', fontSize: 13, fontWeight: 500, cursor: 'pointer' }}>
                {writeForm.is_pinned ? '공지 등록' : '등록'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* 관리자 공지 영역 */}
      {pinnedNotices.length > 0 && (
        <div style={{ marginBottom: 24 }}>
          <div style={{ fontSize: 13, fontWeight: 600, color: '#b45309', marginBottom: 10, display: 'flex', alignItems: 'center', gap: 6 }}>
            <span style={{ fontSize: 14 }}>📌</span> 관리자 공지
          </div>
          <div style={{ background: '#fffbeb', border: '1px solid #fde68a', borderRadius: 12, padding: '4px 0', overflow: 'hidden' }}>
            {pinnedNotices.map((n) => (
              <div key={n.id} style={{ borderBottom: '1px solid #fde68a' }}>
                {renderNotice(n)}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* 자유 게시판 영역 */}
      <div>
        <div style={{ fontSize: 13, fontWeight: 600, color: '#475569', marginBottom: 10 }}>자유 게시판</div>
        {generalNotices.length === 0 ? (
          <div style={{ textAlign: 'center', color: '#94a3b8', padding: '40px 0', fontSize: 14 }}>등록된 글이 없습니다.</div>
        ) : (
          generalNotices.map((n) => renderNotice(n))
        )}
      </div>
    </div>
  );
}
